<script setup>
// ============================================================================
// BonBoZ: bảng "Thống kê chi tiết đánh giá" — hai tab, đúng khuôn Pancake:
//   [Đánh giá theo nhân viên]  ba cột, hai cột sau sắp xếp được
//   [Chi tiết đánh giá]        từng lượt khách chấm sao, mới nhất lên đầu
//
// DÒNG TỔNG hiện năm ngôi sao tô theo điểm trung bình, đúng như Pancake. Sao
// nửa vời được tô một nửa bằng gradient — làm tròn lên thành sao đầy thì 4.1 và
// 4.9 nhìn y hệt nhau.
//
// TRẠNG THÁI RỖNG nói rõ "không có dữ liệu TRONG KHOẢNG THỜI GIAN ĐÃ CHỌN" chứ
// không phải "chưa có đánh giá": hai câu khác nhau, và câu sau làm người dùng
// đi kiểm tra cấu hình trong khi thật ra chỉ cần đổi ngày.
// ============================================================================
import { computed, ref } from 'vue';
import { ngay } from 'dashboard/helper/dinhDangNgay';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  nhanVien: { type: Array, default: () => [] },
  chiTiet: { type: Array, default: () => [] },
  tong: { type: Object, default: null },
  dangTai: { type: Boolean, default: false },
});

const tab = ref('nhan_vien');
const locNguoi = ref(null);
const moLoc = ref(false);
// `null` = chưa bấm sắp xếp, giữ nguyên thứ tự máy chủ trả về (nhiều nhất trước).
const xepTheo = ref(null);
const tangDan = ref(false);

const doiXep = khoa => {
  if (xepTheo.value === khoa) {
    tangDan.value = !tangDan.value;
  } else {
    xepTheo.value = khoa;
    tangDan.value = false;
  }
};

const hangNguoi = computed(() => {
  const ds = props.nhanVien.filter(
    n => !locNguoi.value || n.id === locNguoi.value
  );
  if (!xepTheo.value) return ds;
  const k = xepTheo.value;
  return [...ds].sort((a, b) => (tangDan.value ? a[k] - b[k] : b[k] - a[k]));
});

// `tenLoc` phai dung TRUOC `hangChiTiet`: `const` khong duoc hoisted, va tuy
// computed la lazy nen chay van dung, mot ngay ai do doi sang goi thang la no.
const tenLoc = computed(
  () => props.nhanVien.find(n => n.id === locNguoi.value)?.ten
);
const hangChiTiet = computed(() =>
  props.chiTiet.filter(d => !locNguoi.value || d.nhan_vien === tenLoc.value)
);

// Năm ngôi sao tô theo điểm: sao đầy, sao rỗng, và một sao tô một phần cho
// phần lẻ. `Math.min` chặn ở 1 để 4.9 không tràn sang sao thứ sáu.
const phanSao = diem => i => Math.min(1, Math.max(0, diem - i)) * 100;

const COT_NGUOI = [
  { khoa: 'so_luot', xep: true },
  { khoa: 'trung_binh', xep: true },
];
const TAB = [
  { khoa: 'nhan_vien', nhan: 'TAB_NHAN_VIEN' },
  { khoa: 'chi_tiet', nhan: 'TAB_CHI_TIET' },
];
</script>

<template>
  <section class="border rounded-xl border-n-weak bg-n-solid-2">
    <div class="p-4 pb-0">
      <h3 class="m-0 text-sm font-semibold text-n-slate-12">
        {{ $t('BONBOZ.DANH_GIA.CHI_TIET') }}
      </h3>
      <p class="m-0 mt-0.5 text-xs text-n-slate-11">
        {{ $t('BONBOZ.DANH_GIA.CHI_TIET_MO_TA') }}
      </p>
    </div>

    <div class="flex flex-wrap items-center justify-between gap-3 px-4 pt-3">
      <div class="flex gap-5 -mb-px">
        <button
          v-for="x in TAB"
          :key="x.khoa"
          type="button"
          class="pb-2 text-sm transition-colors border-b-2"
          :class="
            tab === x.khoa
              ? 'border-n-brand text-n-brand font-medium'
              : 'border-transparent text-n-slate-11 hover:text-n-slate-12'
          "
          @click="tab = x.khoa"
        >
          {{ $t(`BONBOZ.DANH_GIA.${x.nhan}`) }}
        </button>
      </div>

      <div class="relative pb-2">
        <Button
          variant="outline"
          color="slate"
          size="sm"
          icon="i-lucide-users"
          trailing-icon="i-lucide-chevron-down"
          :label="tenLoc || $t('BONBOZ.DANH_GIA.TAT_CA_NHAN_VIEN')"
          @click="moLoc = !moLoc"
        />
        <!-- Nền trong suốt phủ toàn màn: bấm ra ngoài là đóng, không cần nghe
             sự kiện trên document rồi phải nhớ gỡ. -->
        <div v-if="moLoc" class="fixed inset-0 z-10" @click="moLoc = false" />
        <div
          v-if="moLoc"
          class="absolute right-0 z-20 flex flex-col gap-1 p-2 mt-1 overflow-y-auto border shadow-lg w-56 max-h-80 rounded-xl border-n-weak bg-n-solid-1"
        >
          <button
            type="button"
            class="px-2 py-1.5 text-sm text-left rounded text-n-slate-12 hover:bg-n-alpha-1"
            @click="
              locNguoi = null;
              moLoc = false;
            "
          >
            {{ $t('BONBOZ.DANH_GIA.TAT_CA_NHAN_VIEN') }}
          </button>
          <button
            v-for="n in nhanVien"
            :key="n.id"
            type="button"
            class="flex items-center gap-2 px-2 py-1.5 text-sm text-left rounded text-n-slate-12 hover:bg-n-alpha-1"
            @click="
              locNguoi = n.id;
              moLoc = false;
            "
          >
            <Avatar :src="n.anh" :name="n.ten" :size="20" rounded-full />
            <span class="truncate">{{ n.ten }}</span>
          </button>
        </div>
      </div>
    </div>

    <div
      v-if="dangTai"
      class="m-4 rounded-lg h-52 bg-n-alpha-2 animate-pulse"
    />

    <!-- ---------------- TAB 1: THEO NHÂN VIÊN ---------------- -->
    <template v-else-if="tab === 'nhan_vien'">
      <div class="overflow-auto border-t max-h-96 border-n-weak">
        <!-- BonBoZ: boc de cuon ngang. Bang nhieu cot hon be ngang man thi bi CAT
             cung chu khong cuon — tren dien thoai mat han cac cot ben phai. Hai man
             Cam xuc va Quang cao da boc dung tu truoc, day la lam not cho deu. -->
        <div class="overflow-x-auto">
          <table class="w-full text-sm border-collapse">
            <thead class="sticky top-0 z-10 bg-n-slate-2">
              <tr class="text-xs border-b border-n-weak text-n-slate-11">
                <th class="px-3 py-2.5 font-medium w-1/3 text-start">
                  {{ $t('BONBOZ.NHAN_VIEN.COT_TEN') }}
                </th>
                <th
                  v-for="c in COT_NGUOI"
                  :key="c.khoa"
                  class="px-3 py-2.5 font-medium border-l border-n-weak text-start"
                >
                  <button
                    type="button"
                    class="inline-flex items-center gap-1 rounded hover:text-n-slate-12"
                    @click="doiXep(c.khoa)"
                  >
                    {{ $t(`BONBOZ.DANH_GIA.C_${c.khoa}`) }}
                    <span
                      class="block size-3.5"
                      :class="
                        xepTheo === c.khoa && tangDan
                          ? 'i-lucide-arrow-up-narrow-wide'
                          : 'i-lucide-arrow-down-wide-narrow'
                      "
                      aria-hidden="true"
                    />
                  </button>
                </th>
              </tr>
            </thead>

            <tbody>
              <tr v-if="!hangNguoi.length">
                <td colspan="3" class="py-20">
                  <span
                    class="flex flex-col items-center gap-2 text-n-slate-10"
                  >
                    <span
                      class="block i-lucide-cloud-off size-10"
                      aria-hidden="true"
                    />
                    <span class="text-sm">
                      {{ $t('BONBOZ.DANH_GIA.KHONG_CO_TRONG_KY') }}
                    </span>
                  </span>
                </td>
              </tr>
              <tr
                v-for="n in hangNguoi"
                :key="n.id"
                class="border-b border-n-weak last:border-b-0 hover:bg-n-alpha-1"
              >
                <td class="px-3 py-2">
                  <span class="flex items-center gap-2">
                    <Avatar
                      :src="n.anh"
                      :name="n.ten"
                      :size="26"
                      rounded-full
                    />
                    <span class="truncate text-n-slate-12">{{ n.ten }}</span>
                  </span>
                </td>
                <td
                  class="px-3 py-2 border-l tabular-nums border-n-weak text-n-slate-12"
                >
                  {{ n.so_luot }}
                </td>
                <td class="px-3 py-2 border-l border-n-weak">
                  <span class="flex items-center gap-1.5">
                    <span class="tabular-nums text-n-slate-12">
                      {{ n.trung_binh.toFixed(1) }}
                    </span>
                    <span
                      class="block i-lucide-star size-3.5 text-n-amber-11"
                      aria-hidden="true"
                    />
                  </span>
                </td>
              </tr>
            </tbody>

            <tfoot v-if="tong">
              <tr
                class="sticky bottom-0 text-sm border-t bg-n-slate-2 border-n-weak"
              >
                <td class="px-3 py-3 font-semibold text-n-slate-12">
                  {{ $t('BONBOZ.TRANG_TK.COT_TONG') }}
                </td>
                <td class="px-3 py-3 border-l border-n-weak">
                  <span
                    class="block font-semibold tabular-nums text-n-slate-12"
                  >
                    {{ tong.so_luot }}
                  </span>
                  <span class="block text-xxs text-n-slate-11">
                    {{ $t('BONBOZ.DANH_GIA.TONG_LUOT') }}
                  </span>
                </td>
                <td class="px-3 py-3 border-l border-n-weak">
                  <!-- Năm ngôi sao tô theo điểm; sao lẻ tô một phần chứ không làm
                       tròn — 4.1 và 4.9 phải nhìn ra khác nhau. -->
                  <span class="flex gap-0.5">
                    <span
                      v-for="i in 5"
                      :key="i"
                      class="relative block size-4"
                      aria-hidden="true"
                    >
                      <span
                        class="absolute inset-0 block i-lucide-star text-n-slate-6"
                      />
                      <span
                        class="absolute inset-0 block overflow-hidden"
                        :style="{
                          width: `${phanSao(tong.trung_binh)(i - 1)}%`,
                        }"
                      >
                        <span
                          class="block i-lucide-star size-4 text-n-amber-11"
                        />
                      </span>
                    </span>
                  </span>
                  <span class="block mt-0.5 text-xxs text-n-slate-11">
                    {{ $t('BONBOZ.TONG_QUAN.DIEM_TRUNG_BINH') }}
                  </span>
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </template>

    <!-- ---------------- TAB 2: CHI TIẾT ---------------- -->
    <div v-else class="overflow-auto border-t max-h-96 border-n-weak">
      <!-- BonBoZ: boc de cuon ngang. Bang nhieu cot hon be ngang man thi bi CAT
           cung chu khong cuon — tren dien thoai mat han cac cot ben phai. Hai man
           Cam xuc va Quang cao da boc dung tu truoc, day la lam not cho deu. -->
      <div class="overflow-x-auto">
        <table class="w-full text-sm border-collapse">
          <thead class="sticky top-0 z-10 bg-n-slate-2">
            <tr class="text-xs border-b border-n-weak text-n-slate-11">
              <th class="px-3 py-2.5 font-medium text-start whitespace-nowrap">
                {{ $t('BONBOZ.CAM_XUC.COT_LUC') }}
              </th>
              <th class="px-3 py-2.5 font-medium text-start">
                {{ $t('BONBOZ.CAM_XUC.COT_KHACH') }}
              </th>
              <th class="px-3 py-2.5 font-medium text-start">
                {{ $t('BONBOZ.NHAN_VIEN.COT_TEN') }}
              </th>
              <th class="px-3 py-2.5 font-medium text-start whitespace-nowrap">
                {{ $t('BONBOZ.DANH_GIA.C_diem') }}
              </th>
              <th class="px-3 py-2.5 font-medium w-1/3 text-start">
                {{ $t('BONBOZ.DANH_GIA.C_gop_y') }}
              </th>
            </tr>
          </thead>

          <tbody>
            <tr v-if="!hangChiTiet.length">
              <td colspan="5" class="py-20">
                <span class="flex flex-col items-center gap-2 text-n-slate-10">
                  <span
                    class="block i-lucide-cloud-off size-10"
                    aria-hidden="true"
                  />
                  <span class="text-sm">
                    {{ $t('BONBOZ.DANH_GIA.KHONG_CO_TRONG_KY') }}
                  </span>
                </span>
              </td>
            </tr>
            <tr
              v-for="d in hangChiTiet"
              :key="d.id"
              class="border-b border-n-weak last:border-b-0 hover:bg-n-alpha-1"
            >
              <td class="px-3 py-2 whitespace-nowrap text-n-slate-11">
                {{ ngay(d.luc * 1000, 'ngayGio') }}
              </td>
              <td class="px-3 py-2 truncate text-n-slate-12">{{ d.khach }}</td>
              <td class="px-3 py-2 truncate text-n-slate-12">
                {{ d.nhan_vien || '—' }}
              </td>
              <td class="px-3 py-2">
                <span class="flex items-center gap-1">
                  <span class="tabular-nums text-n-slate-12">{{ d.diem }}</span>
                  <span
                    class="block i-lucide-star size-3.5 text-n-amber-11"
                    aria-hidden="true"
                  />
                </span>
              </td>
              <td class="px-3 py-2 text-n-slate-11">{{ d.gop_y || '—' }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </section>
</template>
